import { z } from 'zod';
import { ACCOUNTING_TAGS, LEG_ROLES, OPERATION_CATEGORIES } from '../accounting/categories.js';
import { AccountTypeSchema } from './chart-of-accounts.js';
import { AccountNumSchema, IsoDateSchema } from './primitives.js';

export const PcgPayloadAccountSchema = z.object({
  accountNum: AccountNumSchema,
  accountLib: z.string().min(1).max(200),
  accountType: AccountTypeSchema,
  assetId: z.number().int().positive().nullable(),
  parentNum: AccountNumSchema.nullable(),
});

export const PcgPayloadRuleSchema = z.object({
  category: z.enum(OPERATION_CATEGORIES),
  tag: z.enum(ACCOUNTING_TAGS),
  role: z.enum(LEG_ROLES),
  account: AccountNumSchema,
  priority: z.number().int().nonnegative(),
});

export const PcgAssetKeySchema = z.discriminatedUnion('kind', [
  z.object({
    chainSlug: z.string().min(1).max(40),
    kind: z.literal('NATIVE'),
  }),
  z.object({
    chainSlug: z.string().min(1).max(40),
    kind: z.literal('TOKEN'),
    contractAddress: z
      .string()
      .min(1)
      .max(200)
      .transform((address) => address.toLowerCase()),
  }),
]);

export const PcgAssetCodeSchema = z.object({
  assetKey: PcgAssetKeySchema,
  code: z.number().int().positive(),
});

export const PcgPortableAssetAccountSchema = z.object({
  assetKey: PcgAssetKeySchema,
  accountNum: AccountNumSchema,
  accountLib: z.string().min(1).max(200),
  accountType: AccountTypeSchema,
  parentNum: AccountNumSchema.nullable(),
});

const PcgExportPayloadV1Schema = z.object({
  version: z.literal(1),
  exportedAt: z.number().int().positive(),
  effectiveDate: IsoDateSchema,
  accounts: z.array(PcgPayloadAccountSchema).max(5_000),
  rules: z.array(PcgPayloadRuleSchema).max(5_000),
});

export const PcgExportPayloadV2Schema = z.object({
  version: z.literal(2),
  exportedAt: z.number().int().positive(),
  effectiveDate: IsoDateSchema,
  subaccountWidth: z.number().int().min(1).max(8),
  accounts: z.array(PcgPayloadAccountSchema).max(5_000),
  rules: z.array(PcgPayloadRuleSchema).max(5_000),
  assetCodes: z.array(PcgAssetCodeSchema).max(5_000),
  assetAccounts: z.array(PcgPortableAssetAccountSchema).max(20_000),
});

function assetKeyIdentity(assetKey: z.infer<typeof PcgAssetKeySchema>): string {
  return assetKey.kind === 'NATIVE'
    ? `${assetKey.chainSlug}\0NATIVE`
    : `${assetKey.chainSlug}\0TOKEN\0${assetKey.contractAddress}`;
}

export const PcgExportPayloadSchema = z
  .discriminatedUnion('version', [PcgExportPayloadV1Schema, PcgExportPayloadV2Schema])
  .superRefine((payload, context) => {
    if (payload.version !== 2) return;
    const assetKeys = new Set<string>();
    const codes = new Set<number>();
    const codeByAssetKey = new Map<string, number>();
    for (const [index, assignment] of payload.assetCodes.entries()) {
      const key = assetKeyIdentity(assignment.assetKey);
      if (assetKeys.has(key)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetCodes', index, 'assetKey'],
          message: 'Duplicate portable asset key',
        });
      }
      if (codes.has(assignment.code)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetCodes', index, 'code'],
          message: 'Duplicate asset sub-account code',
        });
      }
      if (String(assignment.code).length > payload.subaccountWidth) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetCodes', index, 'code'],
          message: 'Asset code exceeds sub-account width',
        });
      }
      assetKeys.add(key);
      codes.add(assignment.code);
      codeByAssetKey.set(key, assignment.code);
    }

    const accountNums = new Set<string>();
    const genericAccountNums = new Set<string>();
    for (const [index, account] of payload.accounts.entries()) {
      if (accountNums.has(account.accountNum)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accounts', index, 'accountNum'],
          message: 'Duplicate account number',
        });
      }
      accountNums.add(account.accountNum);
      genericAccountNums.add(account.accountNum);
      if (account.assetId !== null) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['accounts', index, 'assetId'],
          message: 'Portable v2 root accounts cannot contain local asset ids',
        });
      }
    }
    for (const [index, account] of payload.assetAccounts.entries()) {
      const key = assetKeyIdentity(account.assetKey);
      const code = codeByAssetKey.get(key);
      if (accountNums.has(account.accountNum)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetAccounts', index, 'accountNum'],
          message: 'Duplicate account number',
        });
      }
      accountNums.add(account.accountNum);
      if (code === undefined) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetAccounts', index, 'assetKey'],
          message: 'Subdivision asset has no portable code',
        });
        continue;
      }
      if (account.parentNum === null) continue;
      if (!genericAccountNums.has(account.parentNum)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetAccounts', index, 'parentNum'],
          message: `Missing generic parent account ${account.parentNum}`,
        });
      }
      const expected = `${account.parentNum}${String(code).padStart(payload.subaccountWidth, '0')}`;
      if (account.accountNum !== expected) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['assetAccounts', index, 'accountNum'],
          message: `Expected account number ${expected}`,
        });
      }
    }
    for (const [index, rule] of payload.rules.entries()) {
      if (!accountNums.has(rule.account)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rules', index, 'account'],
          message: `Missing mapped account ${rule.account}`,
        });
      }
    }
  });

export const PcgImportModeSchema = z.enum(['MERGE', 'REBUILD']);

export const PcgImportRequestSchema = z.object({
  effectiveFrom: IsoDateSchema,
  payload: PcgExportPayloadSchema,
  mode: PcgImportModeSchema.default('MERGE'),
});

export const PcgImportResultSchema = z.object({
  accountsAdded: z.number().int().nonnegative(),
  accountsUpdated: z.number().int().nonnegative(),
  rulesAdded: z.number().int().nonnegative(),
  rulesUpdated: z.number().int().nonnegative(),
  conflicts: z.array(z.string()),
  assetCodesImported: z.number().int().nonnegative().optional(),
  assetAccountsImported: z.number().int().nonnegative().optional(),
  entriesInvalidated: z.number().int().nonnegative().optional(),
});

export type PcgExportPayload = z.infer<typeof PcgExportPayloadSchema>;
export type PcgExportPayloadV2 = z.infer<typeof PcgExportPayloadV2Schema>;
export type PcgImportResult = z.infer<typeof PcgImportResultSchema>;
export type PcgImportMode = z.infer<typeof PcgImportModeSchema>;
export type PcgAssetKey = z.infer<typeof PcgAssetKeySchema>;
export type PcgAssetCode = z.infer<typeof PcgAssetCodeSchema>;
export type PcgPortableAssetAccount = z.infer<typeof PcgPortableAssetAccountSchema>;
export type PcgPayloadAccount = z.infer<typeof PcgPayloadAccountSchema>;
export type PcgPayloadRule = z.infer<typeof PcgPayloadRuleSchema>;
